<template>
  <div class="bg-neutral-50 py-8 min-h-screen">
    <div class="max-w-[1200px] mx-auto px-4 md:px-8">
      <!-- 头部 -->
      <div class="flex items-end justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 class="text-2xl md:text-3xl font-bold text-neutral-900">应用场景案例</h1>
          <p class="text-sm text-neutral-500 mt-1">
            看看大家是怎么把 AI 真正用起来的
          </p>
        </div>
        <button
          class="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 apple-transition"
          @click="goSubmit"
        >
          <i class="fa fa-pencil mr-1"></i> 投稿我的案例
        </button>
      </div>

      <!-- 筛选 -->
      <div
        class="bg-white rounded-xl shadow-sm p-4 mb-6 flex flex-wrap gap-3 items-center"
      >
        <select
          v-model="scenario"
          class="border border-neutral-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500"
          @change="handleSearch"
        >
          <option value="all">全部应用场景</option>
          <option v-for="s in scenarios" :key="s" :value="s">{{ s }}</option>
        </select>
        <div class="relative flex-1 min-w-[200px]">
          <i
            class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm"
          ></i>
          <input
            v-model="searchKey"
            placeholder="搜索主题、关键词或作者"
            class="w-full border border-neutral-200 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-blue-500"
            @input="handleSearch"
          />
        </div>
      </div>

      <!-- 加载 / 空态 -->
      <div v-if="loading" class="text-center py-20 text-neutral-400">
        <i class="fa fa-spinner fa-spin text-2xl"></i>
        <p class="mt-2 text-sm">加载中…</p>
      </div>
      <div v-else-if="list.length === 0" class="text-center py-20 text-neutral-400">
        <i class="fa fa-folder-open-o text-4xl"></i>
        <p class="mt-3 text-sm">还没有案例，来当第一个分享的人吧</p>
      </div>

      <!-- 列表 -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        <div
          v-for="c in list"
          :key="c.id"
          class="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md apple-transition cursor-pointer flex flex-col"
          @click="openDetail(c)"
        >
          <div class="h-40 bg-neutral-100 relative overflow-hidden">
            <button
              @click.stop="toggleFav(c)"
              class="absolute top-2 left-2 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm transition-colors"
              :class="isFav(c.id) ? 'text-primary bg-primary/10' : 'text-neutral-400 hover:text-primary'"
              :title="isFav(c.id) ? '已加入我的应用，点击移出' : '加入我的应用'"
            >
              <i :class="isFav(c.id) ? 'fa fa-check' : 'fa fa-plus'"></i>
            </button>
            <img
              v-if="c.imgCount > 0"
              :src="imgUrl(c, 0)"
              alt=""
              draggable="false"
              class="w-full h-full object-cover case-no-drag"
              @contextmenu.prevent
            />
            <div
              v-else
              class="w-full h-full flex items-center justify-center text-neutral-300"
            >
              <i class="fa fa-picture-o text-3xl"></i>
            </div>
            <span
              v-if="c.awardLevel || c.awardRank"
              class="absolute top-2 right-2 px-2 py-0.5 rounded bg-amber-500 text-white text-xs shadow"
            >
              <i class="fa fa-trophy mr-1"></i>{{ [c.awardLevel, c.awardRank].filter(Boolean).join("") }}
            </span>
            <span
              v-if="c.imgCount > 1"
              class="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/50 text-white text-xs"
            >
              {{ c.imgCount }} 张图
            </span>
          </div>

          <div class="p-4 flex-1 flex flex-col">
            <h3 class="font-bold text-neutral-900 case-clamp-2 mb-2">
              {{ c.title }}
            </h3>
            <div class="flex items-center gap-3 text-xs text-neutral-500 mb-2">
              <span class="truncate"
                ><i class="fa fa-user-o mr-1"></i>{{ c.author }}</span
              >
              <span class="whitespace-nowrap"
                ><i class="fa fa-clock-o mr-1"></i>{{ fmtTime(c.createTime) }}</span
              >
            </div>
            <div class="flex flex-wrap gap-1 mb-2">
              <span class="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-xs">{{
                c.scenario
              }}</span>
              <span
                v-for="k in keywords(c)"
                :key="k"
                class="px-2 py-0.5 rounded bg-neutral-100 text-neutral-600 text-xs"
                >#{{ k }}</span
              >
            </div>
            <p class="text-sm text-neutral-600 case-clamp-3 flex-1">
              {{ c.content }}
            </p>
          </div>
        </div>
      </div>

      <!-- 分页 -->
      <div class="flex justify-center mt-8" v-if="total > pageSize">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="page"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 详情：仅在线浏览，禁用右键、复制与拖拽 -->
    <el-dialog v-model="detailVisible" :title="detail ? detail.title : ''" width="720px" top="6vh">
      <div v-if="detail" class="case-protect relative" @contextmenu.prevent @copy.prevent>
        <!-- 全页文字水印：覆盖正文文字与图片，随内容高度平铺 -->
        <div
          class="case-wm"
          :style="{ backgroundImage: watermarkBg(detail) }"
          aria-hidden="true"
        ></div>
        <div
          class="relative flex items-center gap-3 text-sm text-neutral-500 mb-3 pb-3 border-b border-neutral-100 flex-wrap"
        >
          <img
            v-if="detail.avatar"
            :src="detail.avatar"
            class="w-8 h-8 rounded-full object-cover"
            alt=""
            draggable="false"
          />
          <span class="text-neutral-700 font-medium">{{ detail.author }}</span>
          <span><i class="fa fa-clock-o mr-1"></i>{{ fmtTime(detail.createTime) }}</span>
          <span
            v-if="detail.awardLevel || detail.awardRank"
            class="px-2 py-0.5 rounded bg-amber-50 text-amber-600 text-xs"
          >
            <i class="fa fa-trophy mr-1"></i>{{ [detail.awardLevel, detail.awardRank].filter(Boolean).join("") }}
          </span>
          <button
            @click="toggleFav(detail)"
            class="ml-auto px-3 py-1 rounded-lg border text-xs transition-colors"
            :class="
              isFav(detail.id)
                ? 'border-primary/30 text-primary bg-primary/5'
                : 'border-neutral-300 text-neutral-600 hover:border-primary hover:text-primary'
            "
          >
            <i :class="isFav(detail.id) ? 'fa fa-check' : 'fa fa-plus'" class="mr-1"></i>
            {{ isFav(detail.id) ? "已在我的应用" : "加入我的应用" }}
          </button>
        </div>

        <div class="grid grid-cols-3 gap-y-2 text-sm mb-4">
          <div class="text-neutral-500">应用场景</div>
          <div class="col-span-2 text-neutral-800">{{ detail.scenario }}</div>
          <div class="text-neutral-500">案例关键词</div>
          <div class="col-span-2 text-neutral-800">{{ detail.keywords || "—" }}</div>
        </div>

        <div v-if="detail.imgCount > 0" class="mb-4">
          <div class="text-sm text-neutral-500 mb-2">核心图示</div>
          <div class="grid grid-cols-3 gap-2">
            <img
              v-for="n in detail.imgCount"
              :key="n"
              :src="imgUrl(detail, n - 1)"
              alt=""
              draggable="false"
              class="w-full h-28 object-cover rounded-lg case-no-drag"
              @contextmenu.prevent
            />
          </div>
        </div>

        <div>
          <div class="text-sm text-neutral-500 mb-2">主要内容</div>
          <p class="text-sm text-neutral-800 leading-7 whitespace-pre-wrap">
            {{ detail.content }}
          </p>
        </div>

        <div
          class="mt-4 pt-3 border-t border-neutral-100 text-xs text-neutral-400 flex items-center"
        >
          <i class="fa fa-shield mr-1"></i>
          案例内容仅供在线浏览，已开启防复制与防下载保护
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";

export default {
  name: "CaseSquarePage",
  data() {
    return {
      list: [],
      total: 0,
      page: 1,
      pageSize: 9,
      loading: false,
      /** 当前用户已收藏的案例 id */
      favIds: [],
      scenario: "all",
      searchKey: "",
      searchTimer: null,
      scenarios: [
        "课堂教学",
        "备课教研",
        "作业测评",
        "班级管理",
        "家校沟通",
        "校园活动",
        "行政办公",
        "教师成长",
        "其它",
      ],
      detail: null,
      detailVisible: false,
    };
  },
  created() {
    this.getList();
    this.loadFavIds();
  },
  methods: {
    /** 已登录时拉取收藏 id，用于点亮心形 */
    async loadFavIds() {
      if (!localStorage.getItem("token")) return;
      try {
        const res = await this.$axios({ url: "fav/ids/case" });
        if (res.code === 200) this.favIds = res.data || [];
      } catch (e) {
        /* 未登录或网络异常时静默 */
      }
    },
    isFav(id) {
      return this.favIds.indexOf(id) >= 0;
    },
    /** 加入 / 移出我的应用（个人板块「应用」同步） */
    toggleFav(c) {
      if (!requireLogin(this, "加入我的应用")) return;
      const on = this.isFav(c.id);
      this.$axios({
        url: "fav/case/" + c.id,
        method: on ? "DELETE" : "GET",
      })
        .then((res) => {
          if (res.code !== 200) {
            ElMessage.error(res.msg || "操作失败");
            return;
          }
          if (on) {
            this.favIds = this.favIds.filter((x) => x !== c.id);
            ElMessage.success("已从我的应用移出");
          } else {
            this.favIds.push(c.id);
            ElMessage.success("已加入我的应用，可在个人板块查看");
          }
        })
        .catch(() => ElMessage.error("操作失败，请稍后重试"));
    },
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({
          page: this.page,
          pageSize: this.pageSize,
          scenario: this.scenario,
          searchKey: this.searchKey,
        });
        const res = await this.$axios({
          url: "case/list",
          method: "POST",
          data: { data },
        });
        if (res.code === 200) {
          this.list = res.data.list || [];
          this.total = res.data.total || 0;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    handleSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.getList();
      }, 300);
    },
    handlePageChange(p) {
      this.page = p;
      this.getList();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    openDetail(c) {
      this.detail = c;
      this.detailVisible = true;
    },
    goSubmit() {
      if (!requireLogin(this, "投稿案例")) return;
      this.$router.push("/cases/submit");
    },
    /** 图示走后端加水印的输出接口，不暴露原图地址 */
    imgUrl(c, i) {
      return process.env.VUE_APP_API_ROOT + "/case/image?id=" + c.id + "&i=" + i;
    },
    keywords(c) {
      return (c.keywords || "")
        .split(/[,，\s]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 4);
    },
    fmtTime(t) {
      return t ? String(t).replace("T", " ").substring(0, 16) : "";
    },
    /** 生成平铺水印背景（作者 + 时间，便于追溯截图来源） */
    watermarkBg(c) {
      const text = `小义AI教育工具平台 · ${c.author || ""} · ${this.fmtTime(c.createTime)}`;
      const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="160">` +
        `<text x="150" y="80" fill="#000000" fill-opacity="0.55" font-size="13" ` +
        `font-family="-apple-system,BlinkMacSystemFont,'Microsoft YaHei',sans-serif" ` +
        `text-anchor="middle" transform="rotate(-24 150 80)">${this.escapeXml(text)}</text>` +
        `</svg>`;
      return `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}")`;
    },
    escapeXml(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
    },
  },
};
</script>

<style scoped>
/* 图片不可拖拽 */
.case-no-drag {
  -webkit-user-drag: none;
  user-select: none;
  -webkit-user-select: none;
}

/* 详情区禁止选中复制 */
.case-protect {
  user-select: none;
  -webkit-user-select: none;
}

/* 正文水印层：盖在文字与图片之上，不拦截鼠标事件 */
.case-wm {
  position: absolute;
  inset: 0;
  z-index: 20;
  background-repeat: repeat;
  background-size: 300px 160px;
  opacity: 0.16;
  pointer-events: none;
  user-select: none;
}

.case-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.case-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
